import { type RetailerRegionView, useGetRetailerRegionQuery, useGetRetailersForOrganizationQuery } from '@vizit/api';
import { OTHER_RETAILER_REGION_ID, isOtherRetailerRegion } from '@vizit/constants';
import { useShowUniversalBenchmarks } from '@vizit/feature-flags';
import * as React from 'react';

interface UseOrgRetailersWithOtherProps {
  /** Organization whose retailers are listed */
  organizationId?: string;
  /** Whether the organization's retailer list is fetched at all */
  enabled: boolean;
}

interface UseOrgRetailersWithOtherResult {
  /** The organization's retailers, with Other last when the universal benchmark flag is on */
  retailers: RetailerRegionView[] | undefined;
  /** Error from the organization's retailer list */
  error: unknown;
  /** Whether Other is offered: the universal benchmark flag is on and the retailer list is fetched */
  showOther: boolean;
}

/**
 * The organization's retailers, plus Other (fetched on its own, alongside the list, as the list routes leave it out)
 * when the universal benchmark flag is on. Other is never listed twice, so it keeps working once the list includes it.
 */
function useOrgRetailersWithOther(props: UseOrgRetailersWithOtherProps): UseOrgRetailersWithOtherResult {
  const { organizationId, enabled } = props;

  const showUniversalBenchmarks = useShowUniversalBenchmarks();
  const showOther = enabled && showUniversalBenchmarks;

  const { data: orgRetailers, error } = useGetRetailersForOrganizationQuery(
    { org_id: organizationId ?? '' },
    { enabled: enabled && !!organizationId },
  );

  const orgOther = orgRetailers?.find((retailer) => isOtherRetailerRegion(retailer.id));

  const { data: fetchedOther } = useGetRetailerRegionQuery(
    { retailer_region_id: OTHER_RETAILER_REGION_ID },
    { enabled: showOther && !!organizationId, staleTime: Number.POSITIVE_INFINITY },
  );

  const retailers = React.useMemo(() => {
    if (!orgRetailers || !showOther) return orgRetailers;

    const other = orgOther ?? fetchedOther;
    const listed = orgRetailers.filter((retailer) => !isOtherRetailerRegion(retailer.id));

    return other ? [...listed, other] : listed;
  }, [orgRetailers, showOther, orgOther, fetchedOther]);

  return { retailers, error, showOther };
}

export { useOrgRetailersWithOther };
export type { UseOrgRetailersWithOtherProps, UseOrgRetailersWithOtherResult };
